import React from 'react';
import { uid } from '../helpers.jsx';
import { INPUT_TYPES, DATE_TYPES, C } from '../constants.jsx';
import {
  SH, HR, Row2, Row3, ColorRow, TextInput, NumberInput, Select, TogRow,
  BorderSection, PaddingSection, FBStyleSection, AddButton,
  Collapsible, Slider, Segmented, FontRow, TextStyleSection, ChildMapper,
} from './PanelPrimitives';

const ta = {
  display: 'block', width: '100%', padding: '8px 10px', fontSize: 12,
  border: `1px solid ${C.border}`, borderRadius: 7, color: C.text,
  background: C.surface, outline: 'none', fontFamily: 'inherit',
  resize: 'vertical', lineHeight: 1.5, boxSizing: 'border-box',
};
const inp = {
  display: 'block', width: '100%', padding: '6px 10px', fontSize: 12,
  border: `1px solid ${C.border}`, borderRadius: 7, color: C.text,
  background: C.surface, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
};
const sel = {
  ...inp, cursor: 'pointer', appearance: 'none',
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5'%3E%3Cpath d='M0 0l4 5 4-5z' fill='%236B7280'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center', paddingRight: 28,
};

function updArr(arr, i, patch) {
  return arr.map((item, j) => (j === i ? { ...item, ...patch } : item));
}

// ─── TINY LABEL ──────────────────────────────────────────────────────────────
function TL({ children }) {
  return (
    <label style={{ fontSize: 10, fontWeight: 700, color: C.sub, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}>
      {children}
    </label>
  );
}

// ─── ACTION BUTTONS LIST (page header) ───────────────────────────────────────
function ActionsList({ actions, onChange }) {
  return (
    <>
      {(actions || []).map((a, i) => (
        <div key={i} style={{ display: 'flex', gap: 5, marginBottom: 6, alignItems: 'center' }}>
          <input style={{ ...inp, flex: 1 }} value={a.label} placeholder="Button label"
            onChange={(e) => onChange(updArr(actions, i, { label: e.target.value }))} />
          <select style={{ ...sel, width: 100, flex: 'none' }} value={a.variant}
            onChange={(e) => onChange(updArr(actions, i, { variant: e.target.value }))}>
            {['primary', 'secondary', 'danger'].map((v) => <option key={v}>{v}</option>)}
          </select>
          <button onClick={() => onChange(actions.filter((_, j) => j !== i))}
            style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 18, padding: '0 4px', lineHeight: 1 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = C.red)}
            onMouseLeave={(e) => (e.currentTarget.style.color = C.muted)}>×</button>
        </div>
      ))}
      <AddButton onClick={() => onChange([...(actions || []), { label: 'Action', variant: 'secondary' }])}>
        Add button
      </AddButton>
    </>
  );
}

// ─── COLUMNS LIST (data table) ───────────────────────────────────────────────
function ColumnsList({ columns, onChange }) {
  return (
    <>
      {(columns || []).map((col, i) => (
        <div key={i} style={{ display: 'flex', gap: 5, marginBottom: 5 }}>
          <input style={{ ...inp, flex: 1, fontFamily: 'monospace', fontSize: 11 }} value={col.field}
            placeholder="field_name" onChange={(e) => onChange(updArr(columns, i, { field: e.target.value }))} />
          <input style={{ ...inp, flex: 1 }} value={col.label}
            placeholder="Column Label" onChange={(e) => onChange(updArr(columns, i, { label: e.target.value }))} />
          <button onClick={() => onChange(columns.filter((_, j) => j !== i))}
            style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 18, padding: '0 4px', lineHeight: 1 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = C.red)}
            onMouseLeave={(e) => (e.currentTarget.style.color = C.muted)}>×</button>
        </div>
      ))}
      <AddButton onClick={() => {
        const n = (columns || []).length + 1;
        onChange([...(columns || []), { field: `col${n}`, label: `Column ${n}` }]);
      }}>Add column</AddButton>
    </>
  );
}

// ─── PIE SLICES LIST ─────────────────────────────────────────────────────────
function SlicesList({ slices, onChange }) {
  return (
    <>
      {(slices || []).map((s, i) => (
        <div key={i} style={{ display: 'flex', gap: 5, marginBottom: 6, alignItems: 'center' }}>
          <label style={{ width: 30, height: 28, borderRadius: 6, background: s.color || '#999', border: `1px solid ${C.borderMd}`, cursor: 'pointer', position: 'relative', flexShrink: 0, overflow: 'hidden', display: 'block' }}>
            <input type="color" value={s.color || '#999'}
              onChange={(e) => onChange(updArr(slices, i, { color: e.target.value }))}
              style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }} />
          </label>
          <input style={{ ...inp, flex: 2, fontSize: 12 }} value={s.label} placeholder="Label"
            onChange={(e) => onChange(updArr(slices, i, { label: e.target.value }))} />
          <input type="number" style={{ ...inp, flex: 1, fontSize: 12 }} value={s.value} min={1}
            onChange={(e) => onChange(updArr(slices, i, { value: +e.target.value }))} />
          <button onClick={() => onChange(slices.filter((_, j) => j !== i))}
            style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 18, padding: '0 4px', lineHeight: 1 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = C.red)}
            onMouseLeave={(e) => (e.currentTarget.style.color = C.muted)}>×</button>
        </div>
      ))}
      <AddButton onClick={() => onChange([...(slices || []), { label: `Slice ${(slices || []).length + 1}`, value: 20, color: '#9CA3AF' }])}>
        Add slice
      </AddButton>
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// LAYOUT PANELS
// ═════════════════════════════════════════════════════════════════════════════



// ─── SECTION ─────────────────────────────────────────────────────────────────
export function SectionPanel({ comp, onChange }) {
  return (
    <>
      <Collapsible title="Title" defaultOpen={true}>
        <TextInput label="Title text" value={comp.title} onChange={(v) => onChange({ title: v })} />
        <TogRow label="Show title" value={!!comp.showTitle} onChange={(v) => onChange({ showTitle: v })} />
        <TogRow label="Show divider" value={!!comp.showDivider} onChange={(v) => onChange({ showDivider: v })} />
        <FontRow
          sizeValue={comp.titleFontSize} weightValue={comp.titleFontWeight}
          onSizeChange={(v) => onChange({ titleFontSize: v })}
          onWeightChange={(v) => onChange({ titleFontWeight: v })}
        />
        <ColorRow label="Title colour" value={comp.titleColor} onChange={(v) => onChange({ titleColor: v })} />
        <ColorRow label="Divider colour" value={comp.dividerColor} onChange={(v) => onChange({ dividerColor: v })} />
      </Collapsible>

      <Collapsible title="Card style" defaultOpen={false}>
        <ColorRow label="Background" value={comp.bg} onChange={(v) => onChange({ bg: v })} />
        <Select label="Shadow" value={comp.shadow} onChange={(v) => onChange({ shadow: v })}
          options={[['none', 'None'], ['sm', 'Subtle'], ['md', 'Medium']]} />
        <TogRow label="Show border" value={!!comp.borderEnabled} onChange={(v) => onChange({ borderEnabled: v })} />
      </Collapsible>

      <BorderSection comp={comp} onChange={onChange} />
      <PaddingSection comp={comp} onChange={onChange} />
      <Slider label="Content gap" value={comp.gap} onChange={(v) => onChange({ gap: v })} min={0} max={64} unit="px" />
    </>
  );
}

// ─── FLEX ROW / COL ──────────────────────────────────────────────────────────
export function FlexPanel({ comp, onChange, allComps }) {
  const isRow = comp.type === 'flex_row';
  const cells = comp.cells || [];
  const addCell = () => onChange({
    cells: [...cells, { id: uid(), flex: 1, minSize: 40, bg: 'transparent', label: `Slot ${cells.length + 1}`, compId: null }],
  });
  const delCell = (i) => onChange({ cells: cells.filter((_, j) => j !== i) });
  const updCell = (i, patch) => onChange({ cells: updArr(cells, i, patch) });

  return (
    <>
      <Collapsible title="Flex layout" defaultOpen={true}>
        <Select label="Align items" value={comp.alignItems} onChange={(v) => onChange({ alignItems: v })}
          options={[['flex-start', 'Start'], ['center', 'Center'], ['flex-end', 'End'], ['stretch', 'Stretch'], ['baseline', 'Baseline']]} />
        <Select label="Justify content" value={comp.justifyContent} onChange={(v) => onChange({ justifyContent: v })}
          options={[['flex-start', 'Start'], ['center', 'Center'], ['flex-end', 'End'], ['space-between', 'Space Between'], ['space-around', 'Space Around'], ['space-evenly', 'Space Evenly']]} />
        <TogRow label="Wrap items" value={!!comp.flexWrap} onChange={(v) => onChange({ flexWrap: v })} />
        <Slider label="Gap" value={comp.gap} onChange={(v) => onChange({ gap: v })} min={0} max={80} unit="px" />
      </Collapsible>

      <Collapsible title="Container style" defaultOpen={false}>
        <ColorRow label="Background" value={comp.bg} onChange={(v) => onChange({ bg: v })} />
        <BorderSection comp={comp} onChange={onChange} />
        <PaddingSection comp={comp} onChange={onChange} />
        <TogRow label="Show outlines" value={!!comp.showCellOutlines} onChange={(v) => onChange({ showCellOutlines: v })} />
        <ColorRow label="Default cell border" value={comp.cellBorderColor} onChange={(v) => onChange({ cellBorderColor: v })} />
      </Collapsible>

      <Collapsible
        title={`Slots (${cells.length})`}
        defaultOpen={true}
        badge={cells.filter((c) => c.compId).length || undefined}
      >
        <p style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>
          Add slots, then assign any component from the canvas into each slot.
        </p>
        <ChildMapper
          cells={cells} allComps={allComps || []} parentId={comp.id}
          onUpdate={updCell} onAdd={addCell} onDelete={delCell} cellLabel="Slot"
        />
      </Collapsible>
    </>
  );
}

// ─── GRID CONTAINER ──────────────────────────────────────────────────────────
export function GridPanel({ comp, onChange, allComps }) {
  const cells = comp.cells || [];
  const addCell = () => onChange({
    cells: [...cells, { id: uid(), label: `Cell ${cells.length + 1}`, bg: '#F7F8FA', borderColor: '#CDD0D8', borderRadius: 6, padding: 8, compId: null }],
  });
  const delCell = (i) => onChange({ cells: cells.filter((_, j) => j !== i) });
  const updCell = (i, patch) => onChange({ cells: updArr(cells, i, patch) });

  return (
    <>
      <Collapsible title="Grid structure" defaultOpen={true}>
        <Row2>
          <div>
            <TL>Columns</TL>
            <input type="number" style={inp} min={1} max={12} value={comp.gridCols}
              onChange={(e) => onChange({ gridCols: +e.target.value })} />
          </div>
          <div>
            <TL>Rows</TL>
            <input type="number" style={inp} min={1} max={12} value={comp.gridRows}
              onChange={(e) => onChange({ gridRows: +e.target.value })} />
          </div>
        </Row2>
        <TextInput label="Column template" value={comp.colTemplate} onChange={(v) => onChange({ colTemplate: v })}
          placeholder="1fr 2fr 1fr" hint="grid-template-columns" />
        <TextInput label="Row template" value={comp.rowTemplate} onChange={(v) => onChange({ rowTemplate: v })}
          placeholder="100px auto 200px" hint="grid-template-rows" />
        <Slider label="Gap" value={comp.gap} onChange={(v) => onChange({ gap: v })} min={0} max={80} unit="px" />
        <TogRow label="Show cell outlines" value={!!comp.showCellOutlines} onChange={(v) => onChange({ showCellOutlines: v })} />
      </Collapsible>

      <Collapsible title="Container style" defaultOpen={false}>
        <ColorRow label="Background" value={comp.bg} onChange={(v) => onChange({ bg: v })} />
        <BorderSection comp={comp} onChange={onChange} />
        <PaddingSection comp={comp} onChange={onChange} />
      </Collapsible>

      <Collapsible
        title={`Cells (${cells.length})`}
        defaultOpen={true}
        badge={cells.filter((c) => c.compId).length || undefined}
      >
        <p style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>
          Add cells then assign any canvas component into each. Cells render in grid order left→right, top→bottom.
        </p>
        <ChildMapper
          cells={cells} allComps={allComps || []} parentId={comp.id}
          onUpdate={updCell} onAdd={addCell} onDelete={delCell} cellLabel="Cell"
        />
      </Collapsible>
    </>
  );
}

// ─── FREE DIV ────────────────────────────────────────────────────────────────
export function FreeDivPanel({ comp, onChange }) {
  return (
    <>
      <Collapsible title="Style" defaultOpen={true}>
        <ColorRow label="Background" value={comp.bg} onChange={(v) => onChange({ bg: v })} />
      </Collapsible>
      <BorderSection comp={comp} onChange={onChange} />
      <PaddingSection comp={comp} onChange={onChange} />
    </>
  );
}

// ─── COLUMNS ─────────────────────────────────────────────────────────────────
export function ColumnsPanel({ comp, onChange, allComps }) {
  const cells = comp.cells || [];
  const addCell = () => {
    const nc = [...cells, { id: uid(), label: `Col ${cells.length + 1}`, bg: '#F7F8FA', borderColor: '#CDD0D8', borderRadius: 6, padding: 8, compId: null }];
    onChange({ cells: nc, numCols: nc.length });
  };
  const delCell = (i) => {
    const nc = cells.filter((_, j) => j !== i);
    onChange({ cells: nc, numCols: Math.max(1, nc.length) });
  };
  const updCell = (i, patch) => onChange({ cells: updArr(cells, i, patch) });

  return (
    <>
      <Slider label="Column gap" value={comp.colGap} onChange={(v) => onChange({ colGap: v })} min={0} max={80} unit="px" />
      <Collapsible
        title={`Columns (${cells.length})`}
        defaultOpen={true}
        badge={cells.filter((c) => c.compId).length || undefined}
      >
        <ChildMapper
          cells={cells} allComps={allComps || []} parentId={comp.id}
          onUpdate={updCell} onAdd={addCell} onDelete={delCell} cellLabel="Column"
        />
      </Collapsible>
    </>
  );
}

// ─── DIVIDER ─────────────────────────────────────────────────────────────────
export function DividerPanel({ comp, onChange }) {
  return (
    <>
      <TextInput label="Label text" value={comp.label} onChange={(v) => onChange({ label: v })} placeholder="Optional centre label" />
      <Collapsible title="Line style" defaultOpen={true}>
        <ColorRow label="Line colour" value={comp.lineColor} onChange={(v) => onChange({ lineColor: v })} />
        <Row2>
          <Slider label="Height" value={comp.lineHeight} onChange={(v) => onChange({ lineHeight: v })} min={0.5} max={8} step={0.5} unit="px" />
          <Select label="Style" value={comp.lineStyle || 'solid'} onChange={(v) => onChange({ lineStyle: v })}
            options={[['solid', 'Solid'], ['dashed', 'Dashed'], ['dotted', 'Dotted']]} />
        </Row2>
        <Slider label="Side padding" value={comp.paddingH} onChange={(v) => onChange({ paddingH: v })} min={0} max={60} unit="px" />
      </Collapsible>
      <Collapsible title="Label style" defaultOpen={false}>
        <FontRow sizeValue={comp.labelFontSize} weightValue={400}
          onSizeChange={(v) => onChange({ labelFontSize: v })} onWeightChange={() => {}} />
        <ColorRow label="Text colour" value={comp.labelColor} onChange={(v) => onChange({ labelColor: v })} />
        <ColorRow label="Label background" value={comp.labelBg} onChange={(v) => onChange({ labelBg: v })} />
      </Collapsible>
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// DATA DISPLAY PANELS
// ═════════════════════════════════════════════════════════════════════════════

// ─── STAT CARD ───────────────────────────────────────────────────────────────
export function StatCardPanel({ comp, onChange }) {
  return (
    <>
      <Collapsible title="Content" defaultOpen={true}>
        <TextInput label="Metric label" value={comp.label} onChange={(v) => onChange({ label: v })} placeholder="e.g. Total Revenue" />
        <TextInput label="Value" value={comp.value} onChange={(v) => onChange({ value: v })} placeholder="e.g. ₹2,40,000" />
        <TextInput label="Trend" value={comp.trend} onChange={(v) => onChange({ trend: v })} placeholder="+12.4%" />
        <Select label="Trend direction" value={comp.trendDir} onChange={(v) => onChange({ trendDir: v })}
          options={[['up', '↑ Up (green)'], ['down', '↓ Down (red)']]} />
      </Collapsible>

      <Collapsible title="Value style" defaultOpen={false}>
        <FontRow sizeValue={comp.valueFontSize} weightValue={comp.valueFontWeight}
          onSizeChange={(v) => onChange({ valueFontSize: v })} onWeightChange={(v) => onChange({ valueFontWeight: v })} />
        <ColorRow label="Value colour" value={comp.valueColor} onChange={(v) => onChange({ valueColor: v })} />
      </Collapsible>

      <Collapsible title="Label style" defaultOpen={false}>
        <FontRow sizeValue={comp.labelFontSize} weightValue={comp.labelFontWeight}
          onSizeChange={(v) => onChange({ labelFontSize: v })} onWeightChange={(v) => onChange({ labelFontWeight: v })} />
        <ColorRow label="Label colour" value={comp.labelColor} onChange={(v) => onChange({ labelColor: v })} />
      </Collapsible>

      <Collapsible title="Trend colours" defaultOpen={false}>
        <Row2>
          <ColorRow label="Up colour" value={comp.trendUpColor} onChange={(v) => onChange({ trendUpColor: v })} />
          <ColorRow label="Down colour" value={comp.trendDownColor} onChange={(v) => onChange({ trendDownColor: v })} />
        </Row2>
      </Collapsible>

      <Collapsible title="Card style" defaultOpen={false}>
        <ColorRow label="Background" value={comp.bg} onChange={(v) => onChange({ bg: v })} />
        <TogRow label="Show shadow" value={!!comp.showShadow} onChange={(v) => onChange({ showShadow: v })} />
        <BorderSection comp={comp} onChange={onChange} />
        <PaddingSection comp={comp} onChange={onChange} />
      </Collapsible>
    </>
  );
}

// ─── DATA TABLE ──────────────────────────────────────────────────────────────
export function DataTablePanel({ comp, onChange }) {
  return (
    <>
      <TextInput label="Title" value={comp.title} onChange={(v) => onChange({ title: v })} />

      <Collapsible title={`Columns (${(comp.columns || []).length})`} defaultOpen={true}>
        <ColumnsList columns={comp.columns} onChange={(v) => onChange({ columns: v })} />
      </Collapsible>

      <Collapsible title="Features" defaultOpen={true}>
        <TogRow label="Striped rows" value={!!comp.striped} onChange={(v) => onChange({ striped: v })} />
        <ColorRow label="Stripe colour" value={comp.stripedColor} onChange={(v) => onChange({ stripedColor: v })} />
        <TogRow label="Search bar" value={!!comp.searchable} onChange={(v) => onChange({ searchable: v })} />
        <TogRow label="Pagination" value={!!comp.pagination} onChange={(v) => onChange({ pagination: v })} />
        {comp.pagination && (
          <Slider label="Rows per page" value={comp.pageSize} onChange={(v) => onChange({ pageSize: v })} min={1} max={200} />
        )}
      </Collapsible>

      <Collapsible title="Header style" defaultOpen={false}>
        <ColorRow label="Background" value={comp.headerBg} onChange={(v) => onChange({ headerBg: v })} />
        <ColorRow label="Text colour" value={comp.headerColor} onChange={(v) => onChange({ headerColor: v })} />
        <Slider label="Font size" value={comp.headerFontSize} onChange={(v) => onChange({ headerFontSize: v })} min={8} max={20} unit="px" />
      </Collapsible>

      <Collapsible title="Row style" defaultOpen={false}>
        <ColorRow label="Text colour" value={comp.rowColor} onChange={(v) => onChange({ rowColor: v })} />
        <Slider label="Font size" value={comp.rowFontSize} onChange={(v) => onChange({ rowFontSize: v })} min={8} max={20} unit="px" />
        <Row2>
          <Slider label="Cell pad V" value={comp.cellPadV} onChange={(v) => onChange({ cellPadV: v })} min={0} max={30} unit="px" />
          <Slider label="Cell pad H" value={comp.cellPadH} onChange={(v) => onChange({ cellPadH: v })} min={0} max={40} unit="px" />
        </Row2>
      </Collapsible>

      <Collapsible title="Card style" defaultOpen={false}>
        <ColorRow label="Background" value={comp.bg} onChange={(v) => onChange({ bg: v })} />
        <TogRow label="Show shadow" value={!!comp.showShadow} onChange={(v) => onChange({ showShadow: v })} />
        <BorderSection comp={comp} onChange={onChange} />
      </Collapsible>
    </>
  );
}

// ─── BAR CHART ───────────────────────────────────────────────────────────────
export function BarChartPanel({ comp, onChange }) {
  return (
    <>
      <TextInput label="Chart title" value={comp.title} onChange={(v) => onChange({ title: v })} />
      <Collapsible title="Bar style" defaultOpen={true}>
        <ColorRow label="Bar colour" value={comp.barColor} onChange={(v) => onChange({ barColor: v })} />
        <Slider label="Bar radius" value={comp.barRadius} onChange={(v) => onChange({ barRadius: v })} min={0} max={20} unit="px" />
      </Collapsible>
      <Collapsible title="Grid & title" defaultOpen={false}>
        <TogRow label="Show grid lines" value={!!comp.showGrid} onChange={(v) => onChange({ showGrid: v })} />
        <ColorRow label="Grid colour" value={comp.gridColor} onChange={(v) => onChange({ gridColor: v })} />
        <FontRow sizeValue={comp.titleFontSize} weightValue={600}
          onSizeChange={(v) => onChange({ titleFontSize: v })} onWeightChange={() => {}} />
        <ColorRow label="Title colour" value={comp.titleColor} onChange={(v) => onChange({ titleColor: v })} />
      </Collapsible>
      <Collapsible title="Card style" defaultOpen={false}>
        <ColorRow label="Background" value={comp.bg} onChange={(v) => onChange({ bg: v })} />
        <BorderSection comp={comp} onChange={onChange} />
      </Collapsible>
    </>
  );
}

// ─── LINE CHART ──────────────────────────────────────────────────────────────
export function LineChartPanel({ comp, onChange }) {
  return (
    <>
      <TextInput label="Chart title" value={comp.title} onChange={(v) => onChange({ title: v })} />
      <Collapsible title="Line style" defaultOpen={true}>
        <ColorRow label="Line colour" value={comp.lineColor} onChange={(v) => onChange({ lineColor: v })} />
        <Slider label="Line width" value={comp.lineWidth} onChange={(v) => onChange({ lineWidth: v })} min={0.5} max={8} step={0.5} unit="px" />
        <Slider label="Fill opacity" value={comp.fillOpacity} onChange={(v) => onChange({ fillOpacity: v })} min={0} max={1} step={0.05} />
        <TogRow label="Show data points" value={!!comp.showPoints} onChange={(v) => onChange({ showPoints: v })} />
        <TogRow label="Smooth curve" value={!!comp.smooth} onChange={(v) => onChange({ smooth: v })} />
      </Collapsible>
      <Collapsible title="Grid & title" defaultOpen={false}>
        <TogRow label="Show grid lines" value={!!comp.showGrid} onChange={(v) => onChange({ showGrid: v })} />
        <ColorRow label="Grid colour" value={comp.gridColor} onChange={(v) => onChange({ gridColor: v })} />
        <FontRow sizeValue={comp.titleFontSize} weightValue={600}
          onSizeChange={(v) => onChange({ titleFontSize: v })} onWeightChange={() => {}} />
        <ColorRow label="Title colour" value={comp.titleColor} onChange={(v) => onChange({ titleColor: v })} />
      </Collapsible>
      <Collapsible title="Card style" defaultOpen={false}>
        <ColorRow label="Background" value={comp.bg} onChange={(v) => onChange({ bg: v })} />
        <BorderSection comp={comp} onChange={onChange} />
      </Collapsible>
    </>
  );
}

// ─── PIE CHART ───────────────────────────────────────────────────────────────
export function PieChartPanel({ comp, onChange }) {
  return (
    <>
      <TextInput label="Chart title" value={comp.title} onChange={(v) => onChange({ title: v })} />
      <Collapsible title="Donut" defaultOpen={true}>
        <TogRow label="Donut style" value={!!comp.donut} onChange={(v) => onChange({ donut: v })} />
        {comp.donut && (
          <Slider label="Hole size" value={comp.donutRatio} onChange={(v) => onChange({ donutRatio: v })} min={0.1} max={0.9} step={0.05} />
        )}
      </Collapsible>
      <Collapsible title={`Slices (${(comp.slices || []).length})`} defaultOpen={true}>
        <SlicesList slices={comp.slices} onChange={(v) => onChange({ slices: v })} />
      </Collapsible>
      <Collapsible title="Card style" defaultOpen={false}>
        <ColorRow label="Background" value={comp.bg} onChange={(v) => onChange({ bg: v })} />
        <BorderSection comp={comp} onChange={onChange} />
      </Collapsible>
    </>
  );
}

// ─── BADGE ───────────────────────────────────────────────────────────────────
export function BadgePanel({ comp, onChange }) {
  return (
    <>
      <TextInput label="Text" value={comp.text} onChange={(v) => onChange({ text: v })} />
      <Select label="Variant" value={comp.variant} onChange={(v) => onChange({ variant: v })}
        options={[['success', '✓ Success'], ['warning', '⚠ Warning'], ['danger', '✕ Danger'], ['info', 'ℹ Info'], ['neutral', 'Neutral']]} />
      <Collapsible title="Override style" defaultOpen={false}>
        <ColorRow label="Custom background" value={comp.customBg} onChange={(v) => onChange({ customBg: v })} hint="Overrides variant" />
        <ColorRow label="Custom text colour" value={comp.customColor} onChange={(v) => onChange({ customColor: v })} />
      </Collapsible>
      <Collapsible title="Size & shape" defaultOpen={false}>
        <FontRow sizeValue={comp.fontSz} weightValue={comp.fontW}
          onSizeChange={(v) => onChange({ fontSz: v })} onWeightChange={(v) => onChange({ fontW: v })} />
        <Row2>
          <Slider label="Pad H" value={comp.padH} onChange={(v) => onChange({ padH: v })} min={0} max={40} unit="px" />
          <Slider label="Pad V" value={comp.padV} onChange={(v) => onChange({ padV: v })} min={0} max={24} unit="px" />
        </Row2>
        <Slider label="Border radius" value={comp.radius} onChange={(v) => onChange({ radius: v })} min={0} max={50} unit="px" />
      </Collapsible>
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// FORM PANELS
// ═════════════════════════════════════════════════════════════════════════════

// ─── INPUT FIELD ─────────────────────────────────────────────────────────────
export function InputFieldPanel({ comp, onChange }) {
  return (
    <>
      <TextInput label="Label text" value={comp.labelText} onChange={(v) => onChange({ labelText: v })} />
      <TextInput label="Placeholder" value={comp.placeholder} onChange={(v) => onChange({ placeholder: v })} />
      <Select label="Input type" value={comp.inputType} onChange={(v) => onChange({ inputType: v })}
        options={INPUT_TYPES.map((o) => [o.v, o.l])} />
      {comp.inputType === 'text' && (
        <TextInput label="Max characters" value={comp.maxChars} onChange={(v) => onChange({ maxChars: v })} placeholder="255" />
      )}
      {(comp.inputType === 'integer' || comp.inputType === 'decimal') && (
        <TextInput label="Max value" value={comp.maxVal} onChange={(v) => onChange({ maxVal: v })} placeholder="99999" />
      )}
      <TextInput label="Help text" value={comp.helpText} onChange={(v) => onChange({ helpText: v })} placeholder="Short hint below the field" />
      <TogRow label="Required field" value={!!comp.isMandatory} onChange={(v) => onChange({ isMandatory: v })} />
    </>
  );
}

// ─── DROPDOWN ────────────────────────────────────────────────────────────────
export function DropdownPanel({ comp, onChange }) {
  return (
    <>
      <TextInput label="Label text" value={comp.labelText} onChange={(v) => onChange({ labelText: v })} />
      <TextInput label="Placeholder" value={comp.placeholder} onChange={(v) => onChange({ placeholder: v })} placeholder="Select…" />
      <TogRow label="Multi-select" value={!!comp.isMulti} onChange={(v) => onChange({ isMulti: v })} />

      <Collapsible title="Options source" defaultOpen={true}>
        <Segmented
          value={comp.src || 'static'}
          onChange={(v) => onChange({ src: v })}
          options={[['static', 'Static list'], ['api', 'Dynamic API']]}
        />
        {(!comp.src || comp.src === 'static') ? (
          <div>
            <TL>Options (one per line)</TL>
            <textarea style={{ ...ta, minHeight: 90, fontFamily: 'monospace', fontSize: 11 }}
              value={comp.staticOpts || ''} onChange={(e) => onChange({ staticOpts: e.target.value })} />
          </div>
        ) : (
          <>
            <TextInput label="Schema" value={comp.schema} onChange={(v) => onChange({ schema: v })} placeholder="process_data" mono />
            <TextInput label="Table" value={comp.table} onChange={(v) => onChange({ table: v })} placeholder="departments" mono />
            <TextInput label="Label field" value={comp.labelField} onChange={(v) => onChange({ labelField: v })} placeholder="dept_name" mono />
            <TextInput label="Value field" value={comp.valueField} onChange={(v) => onChange({ valueField: v })} placeholder="dept_id" mono />
            <TextInput label="Filter condition" value={comp.filterCond} onChange={(v) => onChange({ filterCond: v })} placeholder="is_active = 1" mono />
          </>
        )}
      </Collapsible>

      <TextInput label="Help text" value={comp.helpText} onChange={(v) => onChange({ helpText: v })} />
      <TogRow label="Required field" value={!!comp.isMandatory} onChange={(v) => onChange({ isMandatory: v })} />
    </>
  );
}

// ─── DATE PICKER ─────────────────────────────────────────────────────────────
export function DatePickerPanel({ comp, onChange }) {
  return (
    <>
      <TextInput label="Label text" value={comp.labelText} onChange={(v) => onChange({ labelText: v })} />
      <Select label="Date type" value={comp.dateType} onChange={(v) => onChange({ dateType: v })}
        options={DATE_TYPES.map((o) => [o.v, o.l])} />
      <TextInput label="Help text" value={comp.helpText} onChange={(v) => onChange({ helpText: v })} />
      <TogRow label="Required field" value={!!comp.isMandatory} onChange={(v) => onChange({ isMandatory: v })} />
    </>
  );
}

// ─── TEXT AREA ───────────────────────────────────────────────────────────────
export function TextAreaPanel({ comp, onChange }) {
  return (
    <>
      <TextInput label="Label text" value={comp.labelText} onChange={(v) => onChange({ labelText: v })} />
      <TextInput label="Placeholder" value={comp.placeholder} onChange={(v) => onChange({ placeholder: v })} />
      <Slider label="Rows" value={comp.rows} onChange={(v) => onChange({ rows: v })} min={2} max={20} />
      <TextInput label="Help text" value={comp.helpText} onChange={(v) => onChange({ helpText: v })} />
      <TogRow label="Required field" value={!!comp.isMandatory} onChange={(v) => onChange({ isMandatory: v })} />
    </>
  );
}

// ─── FILE UPLOAD ─────────────────────────────────────────────────────────────
export function FileUploadPanel({ comp, onChange }) {
  return (
    <>
      <TextInput label="Label text" value={comp.labelText} onChange={(v) => onChange({ labelText: v })} />
      <TextInput label="Upload path" value={comp.uploadPath} onChange={(v) => onChange({ uploadPath: v })} placeholder="/shared/uploads/" mono />
      <TextInput label="Download path" value={comp.downloadPath} onChange={(v) => onChange({ downloadPath: v })} placeholder="/shared/downloads/" mono />
      <TextInput label="Help text" value={comp.helpText} onChange={(v) => onChange({ helpText: v })} />
      <TogRow label="Required field" value={!!comp.isMandatory} onChange={(v) => onChange({ isMandatory: v })} />
    </>
  );
}

// ─── SEARCH BAR ──────────────────────────────────────────────────────────────
export function SearchBarPanel({ comp, onChange }) {
  return (
    <>
      <TextInput label="Placeholder" value={comp.placeholder} onChange={(v) => onChange({ placeholder: v })} />
      <Collapsible title="Style" defaultOpen={true}>
        <ColorRow label="Background" value={comp.bg} onChange={(v) => onChange({ bg: v })} />
        <ColorRow label="Icon colour" value={comp.iconColor} onChange={(v) => onChange({ iconColor: v })} />
        <ColorRow label="Text colour" value={comp.textColor} onChange={(v) => onChange({ textColor: v })} />
        <Slider label="Font size" value={comp.fontSize} onChange={(v) => onChange({ fontSize: v })} min={8} max={24} unit="px" />
        <Row2>
          <Slider label="Pad H" value={comp.padH} onChange={(v) => onChange({ padH: v })} min={0} max={40} unit="px" />
          <Slider label="Pad V" value={comp.padV} onChange={(v) => onChange({ padV: v })} min={0} max={32} unit="px" />
        </Row2>
        <BorderSection comp={comp} onChange={onChange} />
      </Collapsible>
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ACTION PANELS
// ═════════════════════════════════════════════════════════════════════════════

// ─── BUTTON ──────────────────────────────────────────────────────────────────
export function ButtonPanel({ comp, onChange }) {
  return (
    <>
      <TextInput label="Label" value={comp.label} onChange={(v) => onChange({ label: v })} />
      <TextInput label="On click handler" value={comp.onClick} onChange={(v) => onChange({ onClick: v })} placeholder="handleSubmit" mono />

      <Collapsible title="Colours" defaultOpen={true}>
        <ColorRow label="Background" value={comp.bg} onChange={(v) => onChange({ bg: v })} />
        <ColorRow label="Text colour" value={comp.color} onChange={(v) => onChange({ color: v })} />
        <ColorRow label="Hover background" value={comp.hoverBg} onChange={(v) => onChange({ hoverBg: v })} />
      </Collapsible>

      <Collapsible title="Typography & shape" defaultOpen={false}>
        <FontRow sizeValue={comp.fontSize} weightValue={comp.fontWeight}
          onSizeChange={(v) => onChange({ fontSize: v })} onWeightChange={(v) => onChange({ fontWeight: v })} />
        <Slider label="Border radius" value={comp.borderRadius} onChange={(v) => onChange({ borderRadius: v })} min={0} max={40} unit="px" />
        <Slider label="Horizontal padding" value={comp.padH} onChange={(v) => onChange({ padH: v })} min={0} max={60} unit="px" />
      </Collapsible>

      <Collapsible title="Border" defaultOpen={false}>
        <Slider label="Border width" value={comp.borderWidth} onChange={(v) => onChange({ borderWidth: v })} min={0} max={6} unit="px" />
        <ColorRow label="Border colour" value={comp.borderColor} onChange={(v) => onChange({ borderColor: v })} />
      </Collapsible>
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// CONTENT PANELS
// ═════════════════════════════════════════════════════════════════════════════

// ─── TEXT BLOCK ──────────────────────────────────────────────────────────────
export function TextBlockPanel({ comp, onChange }) {
  return (
    <>
      <div style={{ marginBottom: 14 }}>
        <TL>Content</TL>
        <textarea
          style={{ ...ta, minHeight: 80, fontSize: 13 }}
          value={comp.content || ''}
          onChange={(e) => onChange({ content: e.target.value })}
          placeholder="Type your text here…"
        />
      </div>
      <Select label="Preset style" value={comp.variant} onChange={(v) => {
        const map = { h1: [32, 700], h2: [24, 700], h3: [18, 600], body: [14, 400], small: [12, 400] };
        const [sz, fw] = map[v] || [14, 400];
        onChange({ variant: v, fontSize: sz, fontWeight: fw });
      }} options={[
        ['h1', 'H1 — Page title (32px bold)'],
        ['h2', 'H2 — Heading (24px bold)'],
        ['h3', 'H3 — Subheading (18px semibold)'],
        ['body', 'Body text (14px regular)'],
        ['small', 'Small / caption (12px)'],
      ]} />
      <TextStyleSection comp={comp} onChange={onChange} />
    </>
  );
}

// ─── ALERT BOX ───────────────────────────────────────────────────────────────
export function AlertBoxPanel({ comp, onChange }) {
  return (
    <>
      <Select label="Alert type" value={comp.variant} onChange={(v) => onChange({ variant: v })}
        options={[['info', 'ℹ Info'], ['success', '✓ Success'], ['warning', '⚠ Warning'], ['error', '✕ Error']]} />
      <TogRow label="Show icon" value={!!comp.showIcon} onChange={(v) => onChange({ showIcon: v })} />
      <TextInput label="Title" value={comp.title} onChange={(v) => onChange({ title: v })} />
      <div style={{ marginBottom: 12 }}>
        <TL>Message</TL>
        <textarea style={{ ...ta, minHeight: 70 }} value={comp.message || ''} onChange={(e) => onChange({ message: e.target.value })} />
      </div>
      <Collapsible title="Spacing & text size" defaultOpen={false}>
        <Row2>
          <Slider label="Pad V" value={comp.padV} onChange={(v) => onChange({ padV: v })} min={0} max={40} unit="px" />
          <Slider label="Pad H" value={comp.padH} onChange={(v) => onChange({ padH: v })} min={0} max={40} unit="px" />
        </Row2>
        <Slider label="Border radius" value={comp.borderRadius} onChange={(v) => onChange({ borderRadius: v })} min={0} max={24} unit="px" />
        <Row2>
          <Slider label="Title size" value={comp.titleFontSize} onChange={(v) => onChange({ titleFontSize: v })} min={8} max={24} unit="px" />
          <Slider label="Msg size" value={comp.msgFontSize} onChange={(v) => onChange({ msgFontSize: v })} min={8} max={24} unit="px" />
        </Row2>
      </Collapsible>
    </>
  );
}

// ─── EMPTY STATE ─────────────────────────────────────────────────────────────
export function EmptyStatePanel({ comp, onChange }) {
  return (
    <>
      <Collapsible title="Content" defaultOpen={true}>
        <TextInput label="Icon / emoji" value={comp.icon} onChange={(v) => onChange({ icon: v })} placeholder="◌" />
        <TextInput label="Title" value={comp.title} onChange={(v) => onChange({ title: v })} />
        <div style={{ marginBottom: 12 }}>
          <TL>Message</TL>
          <textarea style={{ ...ta, minHeight: 56 }} value={comp.message || ''} onChange={(e) => onChange({ message: e.target.value })} />
        </div>
        <TextInput label="Button label" value={comp.action} onChange={(v) => onChange({ action: v })} placeholder="Get Started" />
      </Collapsible>

      <Collapsible title="Container style" defaultOpen={false}>
        <ColorRow label="Background" value={comp.bg} onChange={(v) => onChange({ bg: v })} />
        <TogRow label="Show border" value={!!comp.borderEnabled} onChange={(v) => onChange({ borderEnabled: v })} />
        <BorderSection comp={comp} onChange={onChange} />
      </Collapsible>

      <Collapsible title="Icon style" defaultOpen={false}>
        <Slider label="Icon size" value={comp.iconSize} onChange={(v) => onChange({ iconSize: v })} min={16} max={80} unit="px" />
        <ColorRow label="Icon colour" value={comp.iconColor} onChange={(v) => onChange({ iconColor: v })} />
      </Collapsible>

      <Collapsible title="Text style" defaultOpen={false}>
        <Slider label="Title size" value={comp.titleFontSize} onChange={(v) => onChange({ titleFontSize: v })} min={8} max={36} unit="px" />
        <ColorRow label="Title colour" value={comp.titleColor} onChange={(v) => onChange({ titleColor: v })} />
        <Slider label="Message size" value={comp.msgFontSize} onChange={(v) => onChange({ msgFontSize: v })} min={8} max={24} unit="px" />
        <ColorRow label="Message colour" value={comp.msgColor} onChange={(v) => onChange({ msgColor: v })} />
      </Collapsible>

      <Collapsible title="Button style" defaultOpen={false}>
        <ColorRow label="Background" value={comp.actionBg} onChange={(v) => onChange({ actionBg: v })} />
        <ColorRow label="Text colour" value={comp.actionColor} onChange={(v) => onChange({ actionColor: v })} />
        <Slider label="Border radius" value={comp.actionRadius} onChange={(v) => onChange({ actionRadius: v })} min={0} max={40} unit="px" />
      </Collapsible>
    </>
  );
}

// ─── IMAGE BLOCK ─────────────────────────────────────────────────────────────
export function ImageBlockPanel({ comp, onChange }) {
  return (
    <>
      <Collapsible title="Image" defaultOpen={true}>
        <TextInput label="Image URL" value={comp.src} onChange={(v) => onChange({ src: v })} placeholder="https://example.com/image.jpg" />
        <TextInput label="Alt text" value={comp.alt} onChange={(v) => onChange({ alt: v })} />
        <Select label="Object fit" value={comp.objectFit} onChange={(v) => onChange({ objectFit: v })}
          options={[['cover', 'Cover (fill, crop)'], ['contain', 'Contain (no crop)'], ['fill', 'Fill (stretch)']]} />
        <Slider label="Border radius" value={comp.borderRadius} onChange={(v) => onChange({ borderRadius: v })} min={0} max={40} unit="px" />
      </Collapsible>

      <Collapsible title="Caption" defaultOpen={false}>
        <TogRow label="Show caption" value={!!comp.showCaption} onChange={(v) => onChange({ showCaption: v })} />
        <TextInput label="Caption text" value={comp.caption} onChange={(v) => onChange({ caption: v })} />
        <ColorRow label="Text colour" value={comp.capColor} onChange={(v) => onChange({ capColor: v })} />
        <ColorRow label="Background" value={comp.capBg} onChange={(v) => onChange({ capBg: v })} />
        <Row2>
          <Slider label="Font size" value={comp.capFontSize} onChange={(v) => onChange({ capFontSize: v })} min={8} max={24} unit="px" />
          <Select label="Align" value={comp.capAlign} onChange={(v) => onChange({ capAlign: v })}
            options={[['left', 'Left'], ['center', 'Center'], ['right', 'Right']]} />
        </Row2>
      </Collapsible>
    </>
  );
}

// ─── RICH HTML ───────────────────────────────────────────────────────────────
export function RichHtmlPanel({ comp, onChange }) {
  return (
    <>
      <div style={{ marginBottom: 12 }}>
        <TL>HTML content</TL>
        <textarea
          style={{ ...ta, minHeight: 200, fontFamily: 'monospace', fontSize: 11 }}
          value={comp.html || ''}
          onChange={(e) => onChange({ html: e.target.value })}
          placeholder="<p>Your custom HTML here…</p>"
          spellCheck={false}
        />
      </div>
      <Slider label="Padding" value={comp.padding} onChange={(v) => onChange({ padding: v })} min={0} max={60} unit="px" />
    </>
  );
}
